import React from 'react';
import { Show, useRecordContext } from 'react-admin';
import {
  Avatar,
  Box,
  Breadcrumbs,
  Chip,
  Container,
  Divider,
  Grid,
  Link as MuiLink,
  Stack,
  Typography
} from '@mui/material';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { NoteListSection } from '../components/notes/NoteListSection.tsx';
import { DocumentListSection } from '../components/documents/DocumentListSection.tsx';
import { PaginatedCompanyContacts } from './components/PaginatedCompanyContacts.tsx';
import { CompanyRelationshipSection } from './components/CompanyRelationshipSection.tsx';
import { RenewalsSection } from '../renewals/RenewalsSection.tsx';
import {
  DetailRow,
  EmptyState,
  RecordEditButton,
  RecordHeader,
  RecordPage,
  RecordSection,
  RecordTabs
} from '../components/record/RecordPage.tsx';

type CompanyRecord = {
  id?: number;
  name?: string;
  primaryStreet?: string;
  primaryCity?: string;
  primaryState?: string;
  primaryZip?: string;
  websiteUrl?: string;
  notes?: string;
  emails?: { type?: string; email?: string }[];
  phones?: { type?: string; phone?: string }[];
};

const CompanyOverview = ({ record }: { record: CompanyRecord }) => {
  const addressParts = [
    record.primaryStreet,
    record.primaryCity,
    record.primaryState,
    record.primaryZip
  ]
    .map((part) => part?.trim())
    .filter(Boolean);
  const hasContactInfo =
    record.emails?.some((item) => item.email?.trim()) ||
    record.phones?.some((item) => item.phone?.trim());

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 7 }}>
        <Stack spacing={2}>
          <RecordSection
            title="Contact information"
            action={<RecordEditButton label="Edit" variant="text" />}
          >
            {hasContactInfo ? (
              <Stack spacing={0.25} divider={<Divider />}>
                {record.emails?.map((item, index) => {
                  const email = item.email?.trim();
                  return email ? (
                    <DetailRow
                      key={`${email}-${index}`}
                      icon={<EmailOutlinedIcon fontSize="small" />}
                      label={item.type || 'Email'}
                    >
                      <MuiLink href={`mailto:${email}`} sx={{ wordBreak: 'break-all' }}>
                        {email}
                      </MuiLink>
                    </DetailRow>
                  ) : null;
                })}
                {record.phones?.map((item, index) => {
                  const phone = item.phone?.trim();
                  return phone ? (
                    <DetailRow
                      key={`${phone}-${index}`}
                      icon={<PhoneOutlinedIcon fontSize="small" />}
                      label={item.type || 'Phone'}
                    >
                      <MuiLink href={`tel:${phone}`}>{phone}</MuiLink>
                    </DetailRow>
                  ) : null;
                })}
              </Stack>
            ) : (
              <EmptyState>No contact methods on file.</EmptyState>
            )}
          </RecordSection>

          <RecordSection
            title="General notes"
            action={<RecordEditButton label="Edit" variant="text" />}
          >
            {record.notes?.trim() ? (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                <NotesOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.25 }} />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {record.notes.trim()}
                </Typography>
              </Box>
            ) : (
              <EmptyState>No general notes on file.</EmptyState>
            )}
          </RecordSection>
        </Stack>
      </Grid>

      <Grid size={{ xs: 12, md: 5 }}>
        <Stack spacing={2}>
          <RecordSection title="Details" action={<RecordEditButton label="Edit" variant="text" />}>
            <Stack spacing={0.25} divider={<Divider />}>
              <DetailRow label="Company ID">
                <Typography variant="body2">{record.id}</Typography>
              </DetailRow>
              <DetailRow icon={<LanguageOutlinedIcon fontSize="small" />} label="Website">
                {record.websiteUrl?.trim() ? (
                  <MuiLink
                    href={
                      record.websiteUrl.startsWith('http')
                        ? record.websiteUrl
                        : `https://${record.websiteUrl}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ wordBreak: 'break-all' }}
                  >
                    {record.websiteUrl}
                  </MuiLink>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Not set
                  </Typography>
                )}
              </DetailRow>
            </Stack>
          </RecordSection>

          <RecordSection title="Address" action={<RecordEditButton label="Edit" variant="text" />}>
            {addressParts.length ? (
              <DetailRow icon={<LocationOnOutlinedIcon fontSize="small" />} label="Primary">
                <Typography variant="body2">
                  {record.primaryStreet?.trim() ? (
                    <>
                      {record.primaryStreet.trim()}
                      <br />
                    </>
                  ) : null}
                  {[record.primaryCity?.trim(), record.primaryState?.trim()]
                    .filter(Boolean)
                    .join(', ')}
                  {record.primaryZip?.trim() ? ` ${record.primaryZip.trim()}` : ''}
                </Typography>
              </DetailRow>
            ) : (
              <EmptyState>No address on file.</EmptyState>
            )}
          </RecordSection>
        </Stack>
      </Grid>

      {record.id ? (
        <Grid size={{ xs: 12 }}>
          <RenewalsSection
            filter={{ companyId: record.id }}
            description="When each line comes up for the people here. Add them on each person's page."
          />
        </Grid>
      ) : null}

      {record.id ? (
        <Grid size={{ xs: 12 }}>
          <CompanyRelationshipSection companyId={record.id} />
        </Grid>
      ) : null}
    </Grid>
  );
};

const CompanyShowContent = () => {
  const record = useRecordContext<CompanyRecord>();
  if (!record) return null;

  const primaryEmail = record.emails?.find((item) => item.email?.trim())?.email?.trim();
  const primaryPhone = record.phones?.find((item) => item.phone?.trim())?.phone?.trim();
  const website = record.websiteUrl?.trim();

  return (
    <RecordPage>
      <RecordHeader
        avatar={
          <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
            <BusinessOutlinedIcon />
          </Avatar>
        }
        title={record.name?.trim() || 'Company'}
        actions={<RecordEditButton />}
        quickActions={
          primaryEmail || primaryPhone || website ? (
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
              {primaryEmail ? (
                <Chip
                  icon={<EmailOutlinedIcon />}
                  label="Email"
                  component="a"
                  href={`mailto:${primaryEmail}`}
                  clickable
                  variant="outlined"
                  size="small"
                />
              ) : null}
              {primaryPhone ? (
                <Chip
                  icon={<PhoneOutlinedIcon />}
                  label="Call"
                  component="a"
                  href={`tel:${primaryPhone}`}
                  clickable
                  variant="outlined"
                  size="small"
                />
              ) : null}
              {website ? (
                <Chip
                  icon={<LanguageOutlinedIcon />}
                  label="Website"
                  component="a"
                  href={website.startsWith('http') ? website : `https://${website}`}
                  target="_blank"
                  clickable
                  variant="outlined"
                  size="small"
                />
              ) : null}
            </Stack>
          ) : null
        }
      />
      <RecordTabs
        tabs={[
          { value: 'overview', label: 'Overview', content: <CompanyOverview record={record} /> },
          {
            value: 'contacts',
            label: 'Contacts',
            content: (
              <RecordSection
                title="Contacts"
                description="People associated with this company."
                action={<RecordEditButton label="Manage" variant="outlined" returnTab="contacts" />}
              >
                <PaginatedCompanyContacts companyId={record.id as number} />
              </RecordSection>
            )
          },
          {
            value: 'notes',
            label: 'Notes',
            content: (
              <RecordSection
                title="Notes"
                action={
                  <RecordEditButton
                    editPath="notes"
                    label="Manage"
                    variant="outlined"
                    returnTab="notes"
                  />
                }
              >
                <NoteListSection entityType="COMPANY" readOnly hideTitle />
              </RecordSection>
            )
          },
          {
            value: 'documents',
            label: 'Documents',
            content: (
              <RecordSection
                title="Documents"
                action={
                  <RecordEditButton
                    editPath="documents"
                    label="Manage"
                    variant="outlined"
                    returnTab="documents"
                  />
                }
              >
                <DocumentListSection entityType="COMPANY" readOnly hideTitle />
              </RecordSection>
            )
          }
        ]}
      />
    </RecordPage>
  );
};

const CompanyShow: React.FC = () => (
  <Container maxWidth="lg">
    <Breadcrumbs sx={{ mt: 2, mb: 1 }}>
      <MuiLink component={RouterLink} underline="hover" color="inherit" to="/">
        Dashboard
      </MuiLink>
      <MuiLink component={RouterLink} underline="hover" color="inherit" to="/companies">
        Companies
      </MuiLink>
      <Typography color="text.primary">View</Typography>
    </Breadcrumbs>
    <Show actions={false} component="div" sx={{ '& .RaShow-noActions': { mt: 0 } }}>
      <CompanyShowContent />
    </Show>
  </Container>
);

export default CompanyShow;
